/**
 * SmartImprove Engagement Detail — Phase 5.
 *
 * The full consultant workspace. All 6 tabs are now wired:
 *   - Client Inputs (Phase 4)
 *   - Improvement Insights, Recommendations, Implementation, ROI,
 *     Executive Summary (Phase 5 — generated outputs with structured review
 *     state, consultant override actions, and the evidence-chain artifact).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, History } from "lucide-react";
import {
  getEngagement,
  listIssues,
  listKpis,
  listObservations,
  listOperationalAreas,
  listSites,
} from "../lib/smartimproveApi";
import axios from "axios";
import AuditMeta from "../components/AuditMeta";
import InsightsTab from "./smartimprove/tabs/InsightsTab";
import RecommendationsTab from "./smartimprove/tabs/RecommendationsTab";
import ImplementationTab from "./smartimprove/tabs/ImplementationTab";
import RoiTab from "./smartimprove/tabs/RoiTab";
import ExecutiveSummaryTab from "./smartimprove/tabs/ExecutiveSummaryTab";
import PublishBand from "./smartimprove/PublishBand";
import { SitesSection, GroupsSection } from "./smartimprove/SitesGroupsSections";  // SitesSection kept exported for other consumers; GroupsSection still used here.
import ClientInputsWorkspace from "./smartimprove/ClientInputsWorkspace";
import SnapshotPage from "./smartimprove/SnapshotPage";
import { useClientAssignment } from "../lib/clientAssignmentContext";
import ManageClientAccessDialog from "../components/ManageClientAccessDialog";
import { Users, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const TABS = [
  { id: "inputs", label: "Operational Findings", enabled: true },
  { id: "insights", label: "Improvement Insights", enabled: true },
  { id: "recommendations", label: "Recommendations", enabled: true },
  { id: "implementation", label: "Implementation", enabled: true },
  { id: "roi", label: "ROI", enabled: true },
  { id: "exec", label: "Executive Summary", enabled: true },
];

const SEVERITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  critical: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

// Priority badge near the engagement title — matches the Operational
// Findings priority palette so the same word reads identically across the
// workspace.
const PRIORITY_BADGE = {
  low: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  medium: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  high: "bg-[#FED7AA] text-[#9A3412] border-[#FDBA74]",
  critical: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

function SeverityPill({ value }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
        SEVERITY_PILL[value] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
      }`}
    >
      {value}
    </span>
  );
}

export default function SmartImproveDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotVersionId = searchParams.get("snapshot");
  const [tab, setTab] = useState("inputs");
  const [engagement, setEngagement] = useState(null);
  const [areas, setAreas] = useState([]);
  const [issues, setIssues] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [observations, setObservations] = useState([]);
  const [sites, setSites] = useState([]);
  const [runs, setRuns] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const clientAssignment = useClientAssignment();
  const clientReadOnly = !!clientAssignment?.readOnly;
  const [manageAccessOpen, setManageAccessOpen] = useState(false);

  const refreshAll = useCallback(async () => {
    const [eng, isues, ks, obs, sts, rs] = await Promise.all([
      getEngagement(id),
      listIssues(id).catch(() => []),
      listKpis(id).catch(() => []),
      listObservations(id).catch(() => []),
      listSites(id).catch(() => []),
      ax.get(`/engagements/${id}/runs`).then((r) => r.data).catch(() => []),
    ]);
    setEngagement(eng);
    setIssues(isues);
    setKpis(ks);
    setObservations(obs);
    setSites(sts);
    setRuns(rs);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      refreshAll(),
      // Operational areas is shared consultant config; a read-only client is
      // not granted that path, so swallow its 403 and render labels from keys.
      listOperationalAreas()
        .then((a) => (a || []).map((x) => ({ value: x.key, label: x.label })))
        .catch(() => []),
    ])
      .then(([, areaOpts]) => {
        if (!cancelled) setAreas(areaOpts);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load engagement");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshAll]);

  const latestRun = useMemo(() => runs?.[0] || null, [runs]);

  const triggerGenerate = async () => {
    setGenerating(true);
    try {
      // Feb 2026: Claude-backed AI analysis. Loads the active prompt
      // (`smartimprove_transformation_analysis`) from Platform → AI
      // Analysis Prompts, calls Anthropic in the background, persists
      // run + initiatives/recommendations. The deterministic
      // /priva/generate endpoint is preserved but no longer wired here.
      // ROI / Implementation / PDF logic is untouched — the AI engine
      // populates the same smartimprove_groups + smartimprove_recommendations
      // collections those layers already consume.
      const start = await ax.post(`/engagements/${id}/ai-generate`);
      const runId = start.data?.run_id;
      if (!runId) throw new Error("Generation did not return a run_id");

      // Poll the run status. Claude calls take 50-90s, longer than the
      // platform ingress' 60s proxy timeout, so the POST returns
      // immediately and we poll the run row.
      const POLL_INTERVAL_MS = 4000;
      const POLL_MAX_ATTEMPTS = 180; // ~12 minutes ceiling (Claude streams up to ~9 min)
      let final = null;
      for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
        let run;
        try {
          run = (await ax.get(`/ai-runs/${runId}`)).data;
        } catch (_) {
          continue; // transient — retry
        }
        if (run && run.status && run.status !== "running") {
          final = run;
          break;
        }
      }
      if (!final) {
        toast.error("Generation is taking longer than usual. Refresh in a moment to see results.");
        return;
      }
      if (final.status === "succeeded") {
        const c = final.counts || {};
        toast.success(
          `${c.initiatives || 0} workstream${
            (c.initiatives || 0) === 1 ? "" : "s"
          } generated. Review and Accept workstreams, then generate recommendations.`
        );
        setTab("insights");
        await refreshAll();
      } else {
        toast.error(
          final.error_message ||
            "AI analysis failed. Existing analysis was preserved. Check the run log."
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail && typeof detail === "object" && detail.message
          ? detail.message
          : "Failed to generate improvement analysis";
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[#6B7280]" data-testid="smartimprove-detail-loading">
        Loading engagement…
      </div>
    );
  }
  if (!engagement) {
    return (
      <div className="text-sm text-[#6B7280]" data-testid="smartimprove-detail-empty">
        Engagement not found.
        <Link
          to="/consultant/smartimprove"
          className="ml-2 text-[#025979] hover:text-[#03738C]"
        >
          Back to list →
        </Link>
      </div>
    );
  }

  if (snapshotVersionId) {
    return (
      <SnapshotPage
        engagementId={engagement.id}
        versionId={snapshotVersionId}
        isLatestFinalized={engagement?.finalized_version_id === snapshotVersionId}
        clientReadOnly={clientReadOnly}
        onReturnToDraft={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("snapshot");
          setSearchParams(next, { replace: true });
        }}
      />
    );
  }

  // Read-only client view. Primary case: the engagement is still finalized on
  // the shared version → render the SAME workspace tabs the consultant sees,
  // locked (isFinalized). Edge case: the consultant has opened a NEW draft (or
  // finalized a different version) → we must NOT expose the draft, so fall back
  // to the pinned finalized snapshot of the exact version shared via the grant.
  const grantVersionId = clientAssignment?.versionId || null;
  const clientSeesLiveTabs =
    clientReadOnly &&
    !!engagement?.is_finalized &&
    (!grantVersionId || engagement?.finalized_version_id === grantVersionId);
  if (clientReadOnly && !clientSeesLiveTabs) {
    const pinned = grantVersionId || engagement?.finalized_version_id;
    if (pinned) {
      return (
        <SnapshotPage
          engagementId={engagement.id}
          versionId={pinned}
          isLatestFinalized={engagement?.finalized_version_id === pinned}
          clientReadOnly
          onReturnToDraft={() => {}}
        />
      );
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="smartimprove-detail">
      {!clientReadOnly && (
        <Link
          to="/consultant/smartimprove"
          className="inline-flex items-center gap-1 text-xs text-[#025979] hover:text-[#03738C]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to SmartImprove
        </Link>
      )}

      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            WarehouseX · SmartImprove · Engagement
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <h1
              className="text-2xl font-bold text-[#111827]"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="smartimprove-engagement-title"
            >
              {engagement.company_name}
            </h1>
            {engagement.priority ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                  PRIORITY_BADGE[engagement.priority] || ""
                }`}
                data-testid="smartimprove-engagement-priority"
                title="Engagement priority"
              >
                {engagement.priority} Priority
              </span>
            ) : null}
          </div>
          <p className="text-sm text-[#6B7280] mt-1">
            {engagement.contact_name || "—"} · {engagement.email}
          </p>
          <div className="mt-1.5">
            <AuditMeta record={engagement} variant="inline" testId="smartimprove-audit-meta" />
          </div>
        </div>
        {clientReadOnly ? (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 h-7 rounded-sm bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]"
            data-testid="client-readonly-badge"
          >
            <Lock className="w-3.5 h-3.5" /> Read Only
          </span>
        ) : engagement?.is_finalized ? (
          <button
            type="button"
            onClick={() => setManageAccessOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-[#025979] text-[#025979] hover:bg-[#E0F2FE] text-xs font-semibold transition-colors"
            data-testid="manage-client-access-btn"
          >
            <Users className="w-4 h-4" /> Manage Client Access
          </button>
        ) : null}
      </header>

      {!clientReadOnly && (
        <ManageClientAccessDialog
          open={manageAccessOpen}
          onOpenChange={setManageAccessOpen}
          module="smartimprove"
          assignmentId={engagement.id}
          assignmentTitle={engagement.company_name}
        />
      )}

      {/* Iter 88 cosmetic cleanup — the green "Draft generated · Insights …"
          strip was removed per product spec. The empty-state hint below is
          preserved so consultants still see guidance when no analysis has
          been generated yet. Underlying `latestRun` tracking + generation
          APIs are untouched. */}
      {!clientReadOnly && !latestRun ? (
        <section
          className="bg-[#F8FAFC] border border-dashed border-[#E5E7EB] rounded-sm px-4 py-3 text-xs text-[#6B7280] flex items-center gap-2"
          data-testid="smartimprove-detail-run-empty"
        >
          <History className="w-4 h-4" />
          No analysis generated yet. Click
          <strong> Generate Improvement Analysis</strong>
          to run PRIVA on the operational findings below.
        </section>
      ) : null}

      {/* Phase 6 — Publish lifecycle band (between header & tabs, mirrors
          SmartEvaluate's publish band placement). Consultant-only. */}
      {!clientReadOnly && <PublishBand engagement={engagement} onChange={refreshAll} />}

      <nav
        className="flex items-center gap-1 border-b border-[#E5E7EB]"
        data-testid="smartimprove-tabs"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => t.enabled && setTab(t.id)}
            disabled={!t.enabled}
            className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${
              !t.enabled
                ? "text-[#9CA3AF] border-transparent cursor-not-allowed"
                : t.id === tab
                ? "text-[#025979] border-[#025979]"
                : "text-[#374151] border-transparent hover:text-[#025979]"
            }`}
            title={t.enabled ? "" : "Tab ships in the next phase"}
            data-testid={`smartimprove-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <p
        data-testid="smartimprove-tabs-flow-hint"
        className="text-[10.5px] text-[#9CA3AF] italic"
      >
        Follow the flow: Review Operational Findings → Analyze Improvement Insights → Evaluate Recommendations → Build Implementation Plan → Review ROI → View Executive Summary
      </p>

      <fieldset
        disabled={clientReadOnly}
        data-testid="smartimprove-tabs-fieldset"
        className={clientReadOnly ? "si-client-readonly" : ""}
        style={{ minWidth: 0, border: 0, padding: 0, margin: 0 }}
      >
      {tab === "inputs" ? (
        <NewClientInputsTab
          engagement={engagement}
          issues={issues}
          sites={sites}
          onChange={refreshAll}
        />
      ) : null}
      {tab === "insights" ? (
        <InsightsTab
          engagementId={engagement.id}
          issues={issues}
          sites={sites}
          isFinalized={!!engagement?.is_finalized}
          onAIGenerate={triggerGenerate}
          aiGenerating={generating}
        />
      ) : null}
      {tab === "recommendations" ? (
        <RecommendationsTab
          engagementId={engagement.id}
          issues={issues}
          sites={sites}
          isFinalized={!!engagement?.is_finalized}
        />
      ) : null}
      {tab === "implementation" ? (
        <ImplementationTab
          engagementId={engagement.id}
          isFinalized={!!engagement?.is_finalized}
        />
      ) : null}
      {tab === "roi" ? (
        <RoiTab engagementId={engagement.id} isFinalized={!!engagement?.is_finalized} />
      ) : null}
      {tab === "exec" ? (
        <ExecutiveSummaryTab
          engagementId={engagement.id}
          isFinalized={!!engagement?.is_finalized}
        />
      ) : null}
      </fieldset>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Client Inputs tab (Phase 7 refactor)                                       */
/* The legacy inline Add Issue / Add KPI / Add Observation forms are gone.    */
/* The new workspace component owns engagement overview, warehouses, and the  */
/* operational findings table (with one reusable Create/Edit modal).          */
/* SitesSection + GroupsSection stay below for warehouse / enhancement-group  */
/* CRUD — they're collapsed by default unless the engagement already has data */
/* in them.                                                                   */
/* -------------------------------------------------------------------------- */
function NewClientInputsTab({ engagement, issues, sites, onChange }) {
  return (
    <div className="space-y-5">
      <ClientInputsWorkspace
        engagement={engagement}
        sites={sites}
        findings={issues}
        onChange={onChange}
      />
      <GroupsSection
        engagementId={engagement.id}
        isFinalized={!!engagement?.is_finalized}
        onChange={onChange}
      />
    </div>
  );
}

